/**
 * File: PageHeader.tsx
 * Path: apps/admin/src/modules/layout/components/PageHeader.tsx
 * Description: Standard header for feature pages featuring unified path breadcrumb slots.
 * Refactored to handle sticky positioning explicitly on tablet/desktop while scrolling natively on mobile.
 */

import React from "react";
import { useInRouterContext } from "react-router-dom";
import { cn } from "@idk/utils";
import { TourButton } from "@/modules/layout/components/TourButton";

interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: React.ReactNode; // Flexible slot for tokenized breadcrumb chains
  children?: React.ReactNode; // Action control slot (e.g., primary buttons)
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  breadcrumbs,
  children,
  className,
}) => {
  const inRouter = useInRouterContext();
  return (
    <div
      className={cn(
        // Default to relative on mobile, engage sticky anchoring at the `sm` breakpoint
        "relative sm:sticky sm:top-0 z-50 w-full bg-background/95 backdrop-blur-sm transition-all",

        // Structural alignment, margins, and borders mapped directly to theme tokens
        "border-b border-border/60 py-stack-md mb-cluster-sm flex flex-col justify-between gap-stack-lg sm:flex-row sm:items-end",
        className,
      )}
    >
      {/* Structural Title Area utilizing explicit vertical stack definitions */}
      <div className="flex flex-col gap-stack-xs items-start min-w-0 flex-1">
        {/* 1. Breadcrumbs Context Layer (Rendered cleanly above the primary layout heading) */}
        {breadcrumbs && (
          <div className="w-full mb-stack-xs overflow-hidden text-ellipsis whitespace-nowrap">
            {breadcrumbs}
          </div>
        )}

        {/* 2. Primary Title Frame */}
        <h1 className="text-xl sm:text-2xl md:text-3xl font-sans font-bold uppercase tracking-tight text-balance text-foreground leading-none truncate w-full">
          {title}
        </h1>

        {/* 3. Description Segment */}
        {description && (
          <p className="text-sm text-muted-foreground">
            {description}
          </p>
        )}
      </div>

      {/* Right-aligned command panel; the ? icon opens this page's Help dialog */}
      {(children || inRouter) && (
        <div className="flex w-full flex-shrink-0 flex-wrap items-center gap-stack-sm sm:w-auto">
          {children && <div data-tour="page-actions" className="flex flex-wrap items-center gap-stack-sm">{children}</div>}
          {inRouter && <TourButton />}
        </div>
      )}
    </div>
  );
};
